import {
    type KeyboardEvent,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import {
    loadAdminContentItems,
    publishLatestAdminContentBatch
} from "./adminContentRepository.js";

import {
    importAdminContentDrafts,
    loadGrammarExportDrafts
} from "./grammarExport.js";

import {
    AdminContentEditor
} from "./AdminContentEditorView.js";

import {
    ContentFilters,
    ContentInventory
} from "./AdminContentInventory.js";

import {
    ContentImportPanel,
    StatusCard
} from "./AdminContentOverview.js";

import { loadVocabularyExportDrafts } from "./vocabularyExport.js";
import { useAdminText } from "./adminI18n.js";

import type {
    AdminContentDraft
} from "./adminContentRepository.js";

import type {
    AdminContentItemRpcRow,
    CanonicalContentType
} from "../../services/backend/database.types.js";

import type {
    DinoBackendClient
} from "../../services/backend/supabaseClient.js";

import {
    Button,
    Card,
    ProgressBar
} from "../../ui/components/Controls.js";

import {
    Alert
} from "../../ui/components/Feedback.js";

import {
    Page,
    PageHeader,
    Section,
    SectionHeader
} from "../../ui/components/Layout.js";

interface AdminContentWorkspaceProps {
    client: DinoBackendClient;
}

type AdminWorkspaceTab =
    | "content"
    | "imports"
    | "overview";

const adminWorkspaceTabs: ReadonlyArray<{
    id: AdminWorkspaceTab;
    icon: string;
    label: string;
}> = [
    {
        id: "content",
        icon: "✏️",
        label: "Contenus"
    },
    {
        id: "imports",
        icon: "📥",
        label: "Imports"
    },
    {
        id: "overview",
        icon: "📊",
        label: "Vue d’ensemble"
    }
];

function AdminContentWorkspace({
    client
}: AdminContentWorkspaceProps) {
    const adminText = useAdminText();
    const [items, setItems] =
        useState<AdminContentItemRpcRow[]>([]);
    const [loading, setLoading] =
        useState(true);
    const [loadError, setLoadError] =
        useState(false);
    const [refreshCount, setRefreshCount] =
        useState(0);
    const [query, setQuery] =
        useState("");
    const [typeFilter, setTypeFilter] =
        useState<CanonicalContentType | "all">("all");
    const [levelFilter, setLevelFilter] = useState("all");
    const [selectedItem, setSelectedItem] =
        useState<AdminContentItemRpcRow | null>(null);
    const [activeTab, setActiveTab] =
        useState<AdminWorkspaceTab>("content");
    const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
    const [newEditorVersion, setNewEditorVersion] =
        useState(0);
    const pendingSelection = useRef<{
        contentKey: string;
        contentType: CanonicalContentType;
    } | null>(null);
    const [grammarDrafts, setGrammarDrafts] =
        useState<AdminContentDraft[] | null>(null);
    const [vocabularyDrafts, setVocabularyDrafts] = useState<AdminContentDraft[] | null>(null);
    const [importProgress, setImportProgress] =
        useState({ completed: 0, total: 0 });
    const [importStatus, setImportStatus] =
        useState<"idle" | "analysing" | "importing" | "publishing">("idle");
    const [publishConfirmed, setPublishConfirmed] =
        useState(false);
    const [vocabularyProgress, setVocabularyProgress] = useState({ completed: 0, total: 0 });
    const [vocabularyStatus, setVocabularyStatus] = useState<"idle" | "analysing" | "importing" | "publishing">("idle");
    const [vocabularyPublishConfirmed, setVocabularyPublishConfirmed] = useState(false);
    const [message, setMessage] =
        useState<string | null>(null);
    const [error, setError] =
        useState<string | null>(null);

    useEffect(
        () => {
            let active = true;
            setLoading(true);
            setLoadError(false);

            void loadAdminContentItems(client).then(
                loaded => {
                    if (active) {
                        setItems(loaded);
                        setSelectedItem(current => {
                            const identity =
                                pendingSelection.current
                                ?? (current
                                    ? {
                                        contentKey: current.content_key,
                                        contentType: current.content_type
                                    }
                                    : null);

                            pendingSelection.current = null;

                            if (!identity) {
                                return null;
                            }

                            return loaded.find(item =>
                                item.content_key === identity.contentKey
                                && item.content_type === identity.contentType
                            ) ?? null;
                        });
                        setLoading(false);
                    }
                },
                () => {
                    if (active) {
                        setLoadError(true);
                        setLoading(false);
                    }
                }
            );

            return () => {
                active = false;
            };
        },
        [client, refreshCount]
    );

    const filteredItems = useMemo(
        () => {
            const normalized = query.trim()
                .toLocaleLowerCase("fr");

            return items.filter(item =>
                (
                    typeFilter === "all"
                    || item.content_type === typeFilter
                )
                && (
                    typeFilter !== "grammar_lesson"
                    || levelFilter === "all"
                    || item.level === levelFilter
                )
                && (
                    !normalized
                    || item.content_key
                        .toLocaleLowerCase("fr")
                        .includes(normalized)
                    || item.title_fr
                        .toLocaleLowerCase("fr")
                        .includes(normalized)
                )
            );
        },
        [items, query, typeFilter, levelFilter]
    );

    const publishedCount = items.filter(
        item =>
            item.published_revision_number !== null
            && item.archived_at === null
    ).length;
    const pendingCount = items.filter(
        item =>
            item.latest_revision_number
            !== item.published_revision_number
    ).length;
    const pendingGrammarCount = items.filter(
        item =>
            item.content_type === "grammar_lesson"
            && item.latest_revision_number
                !== item.published_revision_number
    ).length;
    const pendingVocabularyCount = items.filter(item => item.content_type === "vocabulary_pack" && item.latest_revision_number !== item.published_revision_number).length;

    function refresh(identity?: {
        contentKey: string;
        contentType: CanonicalContentType;
    }): void {
        pendingSelection.current = identity ?? null;
        setRefreshCount(value => value + 1);
    }

    function handleTabKeyDown(
        event: KeyboardEvent<HTMLButtonElement>,
        currentIndex: number
    ): void {
        let nextIndex: number | null = null;

        if (event.key === "ArrowRight") {
            nextIndex = (currentIndex + 1) % adminWorkspaceTabs.length;
        } else if (event.key === "ArrowLeft") {
            nextIndex = (
                currentIndex - 1 + adminWorkspaceTabs.length
            ) % adminWorkspaceTabs.length;
        } else if (event.key === "Home") {
            nextIndex = 0;
        } else if (event.key === "End") {
            nextIndex = adminWorkspaceTabs.length - 1;
        }

        if (nextIndex === null) {
            return;
        }

        event.preventDefault();
        const nextTab = adminWorkspaceTabs[nextIndex];

        if (!nextTab) {
            return;
        }

        setActiveTab(nextTab.id);
        tabRefs.current[nextIndex]?.focus();
    }

    async function analyseGrammarExport(): Promise<void> {
        setImportStatus("analysing");
        setError(null);
        setMessage(null);

        try {
            const drafts = await loadGrammarExportDrafts();
            setGrammarDrafts(drafts);
            setImportProgress({ completed: 0, total: drafts.length });
        } catch (reason) {
            setError(getErrorMessage(reason));
        } finally {
            setImportStatus("idle");
        }
    }

    async function importGrammar(): Promise<void> {
        if (!grammarDrafts) {
            return;
        }

        setImportStatus("importing");
        setError(null);
        setMessage(null);

        try {
            await importAdminContentDrafts(
                client,
                grammarDrafts,
                (completed, total) => {
                    setImportProgress({ completed, total });
                }
            );
            setMessage(
                `${grammarDrafts.length} leçons importées en brouillons.`
            );
            refresh();
        } catch (reason) {
            setError(getErrorMessage(reason));
        } finally {
            setImportStatus("idle");
        }
    }

    async function publishGrammar(): Promise<void> {
        if (!publishConfirmed) {
            return;
        }

        setImportStatus("publishing");
        setError(null);
        setMessage(null);

        try {
            const count = await publishLatestAdminContentBatch(
                client,
                "grammar_lesson"
            );
            setMessage(`${count} révision(s) Grammaire publiée(s).`);
            setPublishConfirmed(false);
            refresh();
        } catch (reason) {
            setError(getErrorMessage(reason));
        } finally {
            setImportStatus("idle");
        }
    }

    async function analyseVocabularyExport(): Promise<void> {
        setVocabularyStatus("analysing"); setError(null); setMessage(null);
        try { const drafts = await loadVocabularyExportDrafts(); setVocabularyDrafts(drafts); setVocabularyProgress({ completed: 0, total: drafts.length }); }
        catch (reason) { setError(getErrorMessage(reason)); }
        finally { setVocabularyStatus("idle"); }
    }

    async function importVocabulary(): Promise<void> {
        if (!vocabularyDrafts) return;
        setVocabularyStatus("importing"); setError(null); setMessage(null);
        try {
            await importAdminContentDrafts(client, vocabularyDrafts, (completed, total) => setVocabularyProgress({ completed, total }));
            setMessage(`${vocabularyDrafts.length} packs Vocabulaire importés en brouillons.`); refresh();
        } catch (reason) { setError(getErrorMessage(reason)); }
        finally { setVocabularyStatus("idle"); }
    }

    async function publishVocabulary(): Promise<void> {
        if (!vocabularyPublishConfirmed) return;
        setVocabularyStatus("publishing"); setError(null); setMessage(null);
        try { const count = await publishLatestAdminContentBatch(client, "vocabulary_pack"); setMessage(`${count} révision(s) Vocabulaire publiée(s).`); setVocabularyPublishConfirmed(false); refresh(); }
        catch (reason) { setError(getErrorMessage(reason)); }
        finally { setVocabularyStatus("idle"); }
    }

    return (
        <Page>
            <PageHeader
                eyebrow={adminText("Administration")}
                icon="🦕"
                title={adminText("Atelier des contenus")}
                description={adminText("Préparer, relire et publier le corpus canonique sans exposer de clé serveur.")}
                actions={
                    <Button
                        variant="secondary"
                        onClick={() => {
                            setActiveTab("content");
                            setSelectedItem(null);
                            setNewEditorVersion(value => value + 1);
                        }}
                    >
                        + {adminText("Nouveau contenu")}
                    </Button>
                }
            />

            <nav
                aria-label={adminText("Sections de l’atelier")}
                className="mb-5 overflow-x-auto"
            >
                <div
                    className="inline-flex min-w-full gap-1 rounded-card border border-line bg-surface-soft p-1 sm:min-w-0"
                    role="tablist"
                >
                    {adminWorkspaceTabs.map((tab, index) => {
                        const selected = activeTab === tab.id;

                        return (
                            <button
                                aria-controls={`admin-panel-${tab.id}`}
                                aria-selected={selected}
                                className={`min-h-11 flex-1 whitespace-nowrap rounded-control px-3 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-dino-300 sm:flex-none ${selected
                                    ? "bg-surface text-dino-800 shadow-sm"
                                    : "text-muted hover:bg-surface hover:text-ink"
                                }`}
                                id={`admin-tab-${tab.id}`}
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                onKeyDown={event => handleTabKeyDown(event, index)}
                                ref={element => {
                                    tabRefs.current[index] = element;
                                }}
                                role="tab"
                                tabIndex={selected ? 0 : -1}
                                type="button"
                            >
                                <span aria-hidden="true" className="me-2">{tab.icon}</span>
                                {adminText(tab.label)}
                            </button>
                        );
                    })}
                </div>
            </nav>

            {error ? (
                <Alert className="mb-5" variant="danger" title={adminText("Action interrompue")}>
                    {error}
                </Alert>
            ) : null}
            {message ? (
                <Alert className="mb-5" variant="success" title={adminText("Terminé")}>
                    {message}
                </Alert>
            ) : null}

            {activeTab === "overview" ? (
                <Section
                    aria-labelledby="admin-tab-overview"
                    id="admin-panel-overview"
                    role="tabpanel"
                >
                    <div className="grid gap-3 sm:grid-cols-3">
                        <StatusCard icon="📚" label={adminText("Contenus suivis")} value={items.length} />
                        <StatusCard icon="🟢" label={adminText("Publiés")} value={publishedCount} />
                        <StatusCard
                            icon={pendingCount > 0 ? "🟠" : "⚪"}
                            label={adminText("Brouillons en attente")}
                            value={pendingCount}
                        />
                    </div>
                    <Card className="mt-3 p-4">
                        <ProgressBar
                            label={adminText("Couverture publiée")}
                            value={publishedCount}
                            max={Math.max(items.length, 1)}
                            showValue
                        />
                    </Card>
                </Section>
            ) : null}

            {activeTab === "imports" ? (
                <div
                    aria-labelledby="admin-tab-imports"
                    className="grid gap-4 lg:grid-cols-2"
                    id="admin-panel-imports"
                    role="tabpanel"
                >
                    <ContentImportPanel
                        contentLabel={adminText("Grammaire")}
                        itemLabel={adminText("leçon")}
                        drafts={grammarDrafts}
                        pendingCount={pendingGrammarCount}
                        progress={importProgress}
                        publishConfirmed={publishConfirmed}
                        status={importStatus}
                        onAnalyse={() => void analyseGrammarExport()}
                        onImport={() => void importGrammar()}
                        onPublish={() => void publishGrammar()}
                        onPublishConfirmed={setPublishConfirmed}
                    />
                    <ContentImportPanel
                        contentLabel={adminText("Vocabulaire")}
                        itemLabel={adminText("pack")}
                        drafts={vocabularyDrafts}
                        pendingCount={pendingVocabularyCount}
                        progress={vocabularyProgress}
                        publishConfirmed={vocabularyPublishConfirmed}
                        status={vocabularyStatus}
                        onAnalyse={() => void analyseVocabularyExport()}
                        onImport={() => void importVocabulary()}
                        onPublish={() => void publishVocabulary()}
                        onPublishConfirmed={setVocabularyPublishConfirmed}
                    />
                </div>
            ) : null}

            {activeTab === "content" ? (
                <div
                    aria-labelledby="admin-tab-content"
                    className="grid items-start gap-6 xl:grid-cols-[minmax(0,1.05fr)_minmax(24rem,0.95fr)]"
                    id="admin-panel-content"
                    role="tabpanel"
                >
                    <Section className="min-w-0">
                        <SectionHeader
                            title={adminText("Inventaire serveur")}
                            description={`${filteredItems.length} ${adminText("résultat(s)")}`}
                        />
                        <ContentFilters
                            query={query}
                            typeFilter={typeFilter}
                            levelFilter={levelFilter}
                            onQuery={setQuery}
                            onTypeFilter={setTypeFilter}
                            onLevelFilter={setLevelFilter}
                        />
                        <ContentInventory
                            error={loadError}
                            items={filteredItems}
                            loading={loading}
                            onRetry={refresh}
                            onSelect={setSelectedItem}
                        />
                    </Section>

                    <AdminContentEditor
                        availableItems={items}
                        client={client}
                        item={selectedItem}
                        key={selectedItem
                            ? `${selectedItem.content_type}:${selectedItem.content_key}`
                            : `new:${newEditorVersion}`}
                        onChanged={refresh}
                    />
                </div>
            ) : null}
        </Page>
    );
}

function getErrorMessage(reason: unknown): string {
    return reason instanceof Error
        ? reason.message
        : "Une erreur inattendue est survenue.";
}

export {
    AdminContentWorkspace
};
